import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
import "../../../../styles/scss/recital2027SpecialWork.scss";
// 日程の入力はリハーサル日程ページのフォームを共用する（見た目もそのページのスタイル）。
import ScheduleFormDrawer from "./ScheduleFormDrawer";
import "../../../../styles/scss/recital2027RehearsalSchedule.scss";

/**
 * 発表会2027（SLASH GALLERY vol.5）特別ナンバー管理ページ。
 *
 * 設計方針:
 * - 「企画の採否」「出演者」「確定日程」は同じ判断のための別の切り口なので、
 *   別ページに分けず1画面のタブにする。取得も1リクエストにまとめる。
 * - 選考は2段階。企画を採択すると、その企画の応募者は全員 出演確定になる
 *   （出演者数に上限がないため）。定員を超えたときだけ個別に不採用へ外す。
 * - 請求は採択後に人単位で立つ。応募受付中は全員 未請求なので、入金の列は
 *   請求が1件でも発行されるまで出さない（billingStarted で切り替える）。
 * - 請求を発行した人・企画は採否を変更できない。金額は採択確定時に固定される
 *   ため、あとから落とすと請求額と実態がズレる。取り消しはイレギュラーなので
 *   個別対応にする（APIも409で弾く）。
 * - 応募時のリハーサル予定（works.rehearsals）は生徒に公開済みの予定で、
 *   確定日程（recital2027_schedules）はスタジオを押さえた実日程。別物なので
 *   カレンダーでは実線と破線に描き分ける。
 * - 確定日程は地方選抜・HEADZ・特別ナンバーのすべてをこの画面から追加・編集・削除
 *   できる。入力フォームと保存APIはリハーサル日程ページの ScheduleFormDrawer と
 *   create/update/delete_recital2027_schedule をそのまま使い、入力規則を1か所に揃える。
 */

const apiBase = process.env.REACT_APP_API_URL;

const TYPE_LABELS = {
  instructor_only: "①講師コラボ（生徒募集なし）",
  solo_students: "②講師単独＋生徒募集",
  collab_students: "③講師コラボ＋生徒募集",
};
const TYPE_MARKS = { instructor_only: "①", solo_students: "②", collab_students: "③" };
const TYPE_CLASS = { instructor_only: "type-1", solo_students: "type-2", collab_students: "type-3" };

// 応募者の区分。①③は customers から氏名を引く。②③は発表会参加費を別途徴収する。
const KIND_LABELS = { member: "①会員", member_no_entry: "③未申込", external: "②外部" };
const KIND_FULL = {
  member: "①会員（発表会に申込済み）",
  member_no_entry: "③会員（発表会は未申込）",
  external: "②外部",
};

const PAY_LABELS = {
  unbilled: "未請求",
  // 請求済みで完済していないものは期限に関係なく「未払い」と出す
  // （past_due は以前のデータ用で、今は付けない）。
  open: "未払い",
  past_due: "未払い",
  paid: "完済",
  canceled: "取消",
};
const ENTRY_LABELS = { applied: "応募済み", adopted: "出演確定", rejected: "不採用" };
// 企画の採否。CSV でも画面のトグルと同じ言葉で出す。
const WORK_STATUS_LABELS = { selected: "採択", pending: "選考中", rejected: "不採択" };
// 絞り込みに使うエリア。応募時のリハ予定には滋賀も入るので5つ持つ。
const AREAS = ["大阪", "京都", "兵庫", "奈良", "滋賀"];

// 削除確認に出す種別の色と名前。リハーサル日程ページと同じ見分け方にする
// （色の変数 --c-* はそのページのスタイルが持つので、確認ダイアログの中でだけ使う）。
const SCH_AREA_TONE = {
  大阪: "var(--c-osaka)",
  兵庫: "var(--c-hyogo)",
  京都: "var(--c-kyoto)",
  奈良: "var(--c-nara)",
};
const schTone = (s) => {
  if (s.kind === "headz") return "var(--c-headz)";
  if (s.kind === "special") return "var(--c-special)";
  return SCH_AREA_TONE[s.regional_area || s.venue_area] || "var(--accent)";
};
const schKindLabel = (s) => {
  if (s.kind === "headz") return "HEADZ";
  if (s.kind === "special") return "特別ナンバー";
  return `地方選抜 ${s.regional_area || ""}`.trim();
};

// 一覧APIの行（start_time / end_time / studio_name）を、共用フォームが受け取る形
// （start / end / studio）に詰め替える。
const toFormTarget = (s) => ({
  id: s.id,
  kind: s.kind,
  regional_area: s.regional_area,
  special_work_id: s.special_work_id,
  date: s.date,
  start: s.start_time,
  end: s.end_time,
  venue_area: s.venue_area,
  studio: s.studio_name,
  is_booked: s.is_booked,
  note: s.note,
});
const WD = ["日", "月", "火", "水", "木", "金", "土"];
const MONTHS = ["2026-10", "2026-11", "2026-12", "2027-01", "2027-02"];

const yen = (n) => `¥${Number(n || 0).toLocaleString("ja-JP")}`;
const hours = (minutes) => (Number(minutes || 0) / 60).toFixed(1);

// 企画からリハーサルのエリアだけを取り出す（一覧の補助表示）。
const planAreas = (work) => work.rehearsal_areas || [];

// 期日の並び。「いま」はブラウザの日付から決める（締切は動く可能性があるので
// 表示だけの用途にとどめ、操作の可否はサーバー側の状態で判断する）。
const MILESTONES = [
  { date: "2026-09-15", label: "企画応募スタート" },
  { date: "2026-09-25", label: "企画応募締切・全企画公開" },
  { date: "2026-10-10", label: "出演者応募締切" },
  { date: "2026-10-20", label: "採択確定・請求開始" },
  { date: "2026-11-01", label: "入金確認・リハーサル" },
];

// 今日の日付（ブラウザの現地時刻）。toISOString は UTC なので、日本時間の朝9時前だと
// 前日の日付になってしまう。
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

// いまの段階。期日を迎えた最後の段階を光らせる（9/16 なら 9/15、9/25 当日からは 9/25）。
// 最初の期日より前はどれも光らせない（-1）。
const currentStepIndex = (today = todayIso()) => {
  let current = -1;
  MILESTONES.forEach((m, i) => {
    if (m.date <= today) current = i;
  });
  return current;
};

const csvDownload = (filename, rows) => {
  const body = rows
    .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\r\n");
  const blob = new Blob([`﻿${body}`], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

/* ------------------------------------------------------------------ */
/* 小さな表示部品                                                       */
/* ------------------------------------------------------------------ */
const Chipset = ({ options, value, onChange, className = "" }) => (
  <div className={`chipset ${className}`}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        className="chip"
        aria-pressed={value === o.value}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const StatusToggle = ({ options, value, locked, title, onChange }) => (
  <div className={`status-toggle${locked ? " locked" : ""}`} title={title || ""}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        data-v={o.value}
        aria-pressed={value === o.value}
        title={o.title}
        onClick={(e) => {
          e.stopPropagation();
          if (!locked) onChange(o.value);
        }}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const WORK_STATUS_OPTIONS = [
  { value: "selected", label: "採択", title: "採択" },
  { value: "pending", label: "選考", title: "選考中" },
  { value: "rejected", label: "不採", title: "不採択" },
];
const ENTRY_STATUS_OPTIONS = [
  { value: "adopted", label: "確定", title: "出演確定" },
  { value: "applied", label: "保留", title: "応募済み" },
  { value: "rejected", label: "不採", title: "不採用" },
];

const FeeCell = ({ status, amount, billingStarted }) => {
  if (!status) return <span className="dash">対象外</span>;
  if (!billingStarted || status === "unbilled") {
    return <span className="pill unbilled">未請求</span>;
  }
  return (
    <div className="money-cell">
      <span className="amt">{yen(amount)}</span>
      <span className={`pill ${status}`}>{PAY_LABELS[status] || status}</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* 本体                                                                */
/* ------------------------------------------------------------------ */
const SpecialWorkPage = () => {
  useLoginCheck();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [tab, setTab] = useState("works");
  const [selectedWorkId, setSelectedWorkId] = useState(null);

  // 企画タブ
  const [qWorks, setQWorks] = useState("");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fArea, setFArea] = useState("all");
  const [sortKey, setSortKey] = useState("applicants");

  // 出演応募者タブ
  const [qParts, setQParts] = useState("");
  const [fKind, setFKind] = useState("all");
  const [fPay, setFPay] = useState("all");
  const [fWork, setFWork] = useState("all");

  // 日程タブ
  const [month, setMonth] = useState("2026-11");
  const [fSchKind, setFSchKind] = useState("all");
  const [showPlan, setShowPlan] = useState(true);
  const [schArea, setSchArea] = useState("all");
  // 日程の入力フォーム（リハーサル日程ページと共用）。target が null なら新規。
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState(null);
  const [formDate, setFormDate] = useState("");
  // 新規のときの初期値。参照が毎回変わるとフォームが入力中に作り直されるので、
  // 開くときに一度だけ state に置く。
  const [formDefaults, setFormDefaults] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // 請求プレビュー
  const [invoicePreview, setInvoicePreview] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/get_recital2027_special_overview/`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`取得に失敗しました (${res.status})`);
      const json = await res.json();
      setData(json);
      setError("");
      setSelectedWorkId((prev) => {
        if (prev && json.works.some((w) => w.id === prev)) return prev;
        return json.works.length ? json.works[0].id : null;
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const post = useCallback(
    async (path, body) => {
      const res = await fetch(`${apiBase}/api/${path}/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      // 特別ナンバーのAPIは {error}、共用の日程APIは {status:"error", message} で返す。
      if (!res.ok || json.status === "error") {
        throw new Error(json.error || json.message || `失敗しました (${res.status})`);
      }
      return json;
    },
    []
  );

  // 参照が毎回変わると下の useMemo が無意味になるので、配列はここで固定する。
  const works = useMemo(() => data?.works || [], [data]);
  const entrants = useMemo(() => data?.entrants || [], [data]);
  const schedules = useMemo(() => data?.schedules || [], [data]);
  const conflicts = useMemo(() => data?.conflicts || [], [data]);
  const summary = data?.summary || {};
  const maxSlots = data?.max_selected_works || 13;

  // 請求が1件でも出ていれば「採択後」の画面にする。応募受付中は入金の列を出しても
  // 全部 未請求で埋まるだけなので、応募人数と区分の内訳を主にする。
  const billingStarted = (summary.billed || 0) > 0;

  const workById = useMemo(() => {
    const map = {};
    works.forEach((w) => {
      map[w.id] = w;
    });
    return map;
  }, [works]);

  // 確定日程が重なっている出演者。日程タブと出演者タブの両方で印を出す。
  const clashEntrantIds = useMemo(() => {
    const set = new Set();
    conflicts.forEach((c) => (c.entrant_ids || []).forEach((id) => set.add(id)));
    return set;
  }, [conflicts]);

  const clashScheduleIds = useMemo(() => {
    const set = new Set();
    conflicts.forEach((c) => (c.schedule_ids || []).forEach((id) => set.add(id)));
    return set;
  }, [conflicts]);

  const entriesByWork = useMemo(() => {
    const map = {};
    entrants.forEach((e) => {
      (e.entries || []).forEach((x) => {
        (map[x.work_id] = map[x.work_id] || []).push({ ...x, entrant: e });
      });
    });
    Object.values(map).forEach((rows) =>
      rows.sort((a, b) => (a.entrant.kana || "").localeCompare(b.entrant.kana || "", "ja"))
    );
    return map;
  }, [entrants]);

  /* ---------------- 操作 ---------------- */
  const flash = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 4000);
  };

  const changeWorkStatus = async (workId, status) => {
    try {
      await post("update_recital2027_special_work_status", { id: workId, status });
      await fetchOverview();
    } catch (e) {
      flash(e.message);
    }
  };

  const changeEntryStatus = async (entryId, status) => {
    try {
      await post("update_recital2027_special_entry_status", { id: entryId, status });
      await fetchOverview();
    } catch (e) {
      flash(e.message);
    }
  };

  const openCreateSchedule = (date, defaults) => {
    setFormTarget(null);
    setFormDate(date || "");
    setFormDefaults(defaults || null);
    setFormOpen(true);
  };

  const openEditSchedule = (row) => {
    setFormTarget(toFormTarget(row));
    setFormDate("");
    setFormDefaults(null);
    setFormOpen(true);
  };

  const handleScheduleSaved = async () => {
    setFormOpen(false);
    await fetchOverview();
  };

  // 削除は確認を挟む（リハーサル日程ページと同じ）。実体は論理削除で行はDBに残る。
  const handleScheduleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await post("delete_recital2027_schedule", { id: deleteTarget.id });
      setDeleteTarget(null);
      await fetchOverview();
    } catch (e) {
      flash(`削除に失敗しました: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const loadInvoicePreview = async () => {
    try {
      const res = await fetch(`${apiBase}/api/preview_recital2027_special_invoices/`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`取得に失敗しました (${res.status})`);
      setInvoicePreview(await res.json());
    } catch (e) {
      flash(e.message);
    }
  };

  /* ---------------- 企画一覧の絞り込み ---------------- */
  const filteredWorks = useMemo(() => {
    const q = qWorks.trim().toLowerCase();
    const rows = works.filter((w) => {
      if (fType !== "all" && w.entry_type !== fType) return false;
      if (fStatus !== "all" && w.status !== fStatus) return false;
      if (fArea !== "all" && !planAreas(w).includes(fArea)) return false;
      if (q) {
        const hay = [w.title, w.genre, ...(w.instructors || []).map((i) => i.name)]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const by = {
      applicants: (a, b) => b.counts.total - a.counts.total,
      paid: (a, b) =>
        b.counts.paid / Math.max(1, b.counts.adopted) -
        a.counts.paid / Math.max(1, a.counts.adopted),
      title: (a, b) => (a.title || "").localeCompare(b.title || "", "ja"),
      updated: (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
    }[sortKey];
    return [...rows].sort(by);
  }, [works, qWorks, fType, fStatus, fArea, sortKey]);

  /* ---------------- 出演応募者の絞り込み ---------------- */
  const filteredEntrants = useMemo(() => {
    const q = qParts.trim().toLowerCase();
    return entrants
      .filter((e) => {
        if (fKind !== "all" && e.entrant_type !== fKind) return false;
        if (fPay === "unpaid") {
          const open = ["open", "past_due"];
          if (!open.includes(e.special_fee_status) && !open.includes(e.entry_fee_status))
            return false;
        }
        if (fPay === "multi" && (e.entries || []).length < 2) return false;
        if (fPay === "clash" && !clashEntrantIds.has(e.id)) return false;
        if (fWork !== "all" && !(e.entries || []).some((x) => String(x.work_id) === fWork))
          return false;
        if (q && ![e.name, e.kana, String(e.customer_id || "")].join(" ").toLowerCase().includes(q))
          return false;
        return true;
      })
      .sort(
        (a, b) =>
          b.special_fee_amount - a.special_fee_amount ||
          (a.kana || "").localeCompare(b.kana || "", "ja")
      );
  }, [entrants, qParts, fKind, fPay, fWork, clashEntrantIds]);

  /* ---------------- カレンダー ---------------- */
  const calendar = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    const byDay = {};

    schedules.forEach((s) => {
      if (fSchKind !== "all" && s.kind !== fSchKind) return;
      if (schArea !== "all" && s.venue_area !== schArea) return;
      if (!s.date.startsWith(month)) return;
      const day = Number(s.date.slice(8, 10));
      (byDay[day] = byDay[day] || []).push({ type: "schedule", schedule: s });
    });

    if (showPlan && (fSchKind === "all" || fSchKind === "special")) {
      works.forEach((w) => {
        (w.rehearsals || []).forEach((p) => {
          if (!p.date || !p.date.startsWith(month)) return;
          if (schArea !== "all" && p.area !== schArea) return;
          // 同じ日に確定日程が入っている企画は、応募時の予定を重ねて描かない
          const settled = schedules.some(
            (s) => s.kind === "special" && s.special_work_id === w.id && s.date === p.date
          );
          if (settled) return;
          const day = Number(p.date.slice(8, 10));
          (byDay[day] = byDay[day] || []).push({ type: "plan", work: w, plan: p });
        });
      });
    }

    const cells = [];
    for (let i = 0; i < first.getDay(); i += 1) cells.push(null);
    for (let d = 1; d <= last.getDate(); d += 1) cells.push(d);
    while (cells.length % 7) cells.push(null);

    Object.values(byDay).forEach((list) =>
      list.sort((a, b) => {
        const at = a.type === "schedule" ? a.schedule.start_time : a.plan.start_time;
        const bt = b.type === "schedule" ? b.schedule.start_time : b.plan.start_time;
        return (at || "").localeCompare(bt || "");
      })
    );

    return { cells, byDay };
  }, [month, schedules, works, fSchKind, showPlan, schArea]);

  const monthSchedules = useMemo(
    () => schedules.filter((s) => s.date.startsWith(month)),
    [schedules, month]
  );

  const studentWorks = useMemo(() => works.filter((w) => w.recruits_students), [works]);
  // 共用フォームの作品リスト。リハーサル日程ページと同じく取り下げ以外の全作品を
  // ID順で出す（採択前でも日程を仮置きできるよう、類型や採否では絞らない）。
  const formWorks = useMemo(
    () =>
      [...works]
        .sort((a, b) => a.id - b.id)
        .map((w) => ({ id: w.id, title: w.title || "（タイトル未入力）" })),
    [works]
  );
  const maxApplicants = useMemo(
    () => Math.max(12, ...works.map((w) => w.counts.total)),
    [works]
  );
  const selectedWork = selectedWorkId ? workById[selectedWorkId] : null;

  /* ---------------- CSV ---------------- */
  const exportWorksCsv = () => {
    csvDownload(
      "recital2027_special_works.csv",
      [
        ["ID", "類型", "採否", "作品", "ジャンル", "講師", "応募", "出演確定", "完済", "更新", "出演確定者"],
        ...filteredWorks.map((w) => [
          w.id,
          TYPE_LABELS[w.entry_type],
          WORK_STATUS_LABELS[w.status] || w.status,
          w.title,
          w.genre,
          (w.instructors || []).map((i) => i.name).join(" / "),
          w.counts.total,
          w.counts.adopted,
          w.counts.paid,
          w.updated_at,
          // 出演確定した人の氏名（カナ順）。名前が長く列が伸びるので最後に置く。
          (entriesByWork[w.id] || [])
            .filter((x) => x.status === "adopted")
            .map((x) => x.entrant.name)
            .join(" / "),
        ]),
      ]
    );
  };

  const exportEntrantsCsv = () => {
    csvDownload(
      "recital2027_special_entrants.csv",
      [
        ["ID", "氏名", "カナ", "区分", "会員ID", "メール", "電話", "支払方法", "応募ナンバー", "発表会参加費", "発表会参加費状況", "特別ナンバー参加費", "状況", "応募日時"],
        ...filteredEntrants.map((e) => [
          e.id,
          e.name,
          e.kana,
          KIND_FULL[e.entrant_type],
          e.customer_id || "",
          e.mail,
          e.phone,
          e.payment_method === "lump" ? "一括" : "分割4回",
          (e.entries || []).map((x) => workById[x.work_id]?.title || x.work_id).join(" / "),
          e.entry_fee_amount || "",
          PAY_LABELS[e.entry_fee_status] || "",
          e.special_fee_amount || "",
          PAY_LABELS[e.special_fee_status] || "",
          e.applied_at,
        ]),
      ]
    );
  };

  if (loading) {
    return (
      <div className="recital2027-special-page">
        <p className="loading">読み込み中…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recital2027-special-page">
        <div className="alert">
          <Icon name="warning sign" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const stepIndex = currentStepIndex();

  return (
    <>
    <div className="recital2027-special-page">
      {notice && (
        <div className="toast">
          <Icon name="info circle" />
          <span>{notice}</span>
        </div>
      )}

      <header className="page-header">
        <div className="header-left">
          <p className="eyebrow">Recital 2027 / SLASH GALLERY vol.5</p>
          <h1>特別ナンバー 企画管理</h1>
          <p className="header-sub">
            企画の採否、出演者、請求と入金、確定日程を1画面で扱う。
          </p>
        </div>
        <div className="slot-gauge">
          <div className="slot-top">
            <span className="slot-label">採択枠</span>
            <span className="slot-count">
              {summary.works_selected || 0}
              <small>/{maxSlots}</small>
            </span>
          </div>
          <div className="slot-bar">
            {Array.from({ length: maxSlots }, (_, i) => (
              <span
                key={i}
                className={`slot-cell${i < (summary.works_selected || 0) ? " on" : ""}`}
              />
            ))}
          </div>
          <p className="slot-note">全100ナンバー中、特別ナンバーは最大{maxSlots}</p>
        </div>
      </header>

      <div className="timeline">
        {MILESTONES.map((s, i) => (
          <div
            key={s.date}
            className={`tl-step${i < stepIndex ? " done" : ""}${i === stepIndex ? " now" : ""}`}
          >
            <span className="d">{s.date.slice(5).replace("-", "/")}</span>
            <span className="l">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="kpis">
        {!billingStarted ? (
          <>
            <div className="kpi">
              <span className="k">応募企画</span>
              <span className="v">
                {summary.works_total}
                <small>件</small>
              </span>
              <span className="sub">
                採択{summary.works_selected} ・ 選考中{summary.works_pending} ・ 不採択
                {summary.works_rejected}
              </span>
            </div>
            <div className="kpi">
              <span className="k">残りの枠</span>
              <span className="v">
                {summary.slots_left}
                <small>枠</small>
              </span>
              <span className="sub">上限{maxSlots}ナンバー</span>
            </div>
            <div className="kpi">
              <span className="k">出演応募者</span>
              <span className="v">
                {summary.entrants_total}
                <small>名</small>
              </span>
              <span className="sub">
                延べ{summary.entries_total}件 ・ 掛け持ち{summary.entrants_multi}名
              </span>
            </div>
            <div className="kpi">
              <span className="k">区分の内訳</span>
              <span className="v small">
                {summary.entrants_member} / {summary.entrants_member_no_entry} /{" "}
                {summary.entrants_external}
              </span>
              <span className="sub">①会員 / ③未申込 / ②外部</span>
            </div>
            <div className="kpi warn">
              <span className="k">発表会参加費の徴収対象</span>
              <span className="v">
                {summary.needs_entry_fee}
                <small>名</small>
              </span>
              <span className="sub">②③に{yen(data.fees.entry_fee)}（税込）を別途請求</span>
            </div>
            <div className="kpi">
              <span className="k">確定日程</span>
              <span className="v">
                {summary.schedules_special}
                <small>コマ</small>
              </span>
              <span className="sub">未予約{summary.schedules_unbooked}コマ</span>
            </div>
          </>
        ) : (
          <>
            <div className="kpi">
              <span className="k">採択ナンバー</span>
              <span className="v">
                {summary.works_selected}
                <small>/{maxSlots}</small>
              </span>
              <span className="sub">応募企画{summary.works_total}件</span>
            </div>
            <div className="kpi">
              <span className="k">出演確定</span>
              <span className="v">
                {summary.entrants_adopted}
                <small>名</small>
              </span>
              <span className="sub">応募{summary.entrants_total}名</span>
            </div>
            <div className="kpi">
              <span className="k">請求総額</span>
              <span className="v small">{yen(summary.billed_amount)}</span>
              <span className="sub">{summary.billed}名に発行済み</span>
            </div>
            <div className="kpi">
              <span className="k">完済</span>
              <span className="v">
                {summary.paid}
                <small>/{summary.billed}</small>
              </span>
              <span className="sub">残{Math.max(0, summary.billed - summary.paid)}名</span>
            </div>
            <div className="kpi bad">
              <span className="k">未払い</span>
              <span className="v">
                {summary.unpaid}
                <small>名</small>
              </span>
              <span className="sub">督促の対象</span>
            </div>
            <div className="kpi warn">
              <span className="k">スタジオ未予約</span>
              <span className="v">
                {summary.schedules_unbooked}
                <small>コマ</small>
              </span>
              <span className="sub">確定日程{summary.schedules_special}コマ中</span>
            </div>
          </>
        )}
      </div>

      <div className="tabrow">
        <div className="tabs" role="tablist">
          {[
            { key: "works", label: "企画", count: works.length },
            { key: "parts", label: "出演応募者", count: entrants.length },
            { key: "sch", label: "日程", count: schedules.length },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              className="tab"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <span className="c">{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ============ 企画 ============ */}
      {tab === "works" && (
        <section>
          <div className="toolbar">
            <label className="search-bar">
              <Icon name="search" />
              <input
                type="text"
                value={qWorks}
                placeholder="作品名・ジャンル・講師名"
                onChange={(e) => setQWorks(e.target.value)}
              />
            </label>
            <Chipset
              value={fType}
              onChange={setFType}
              options={[
                { value: "all", label: "全類型" },
                { value: "instructor_only", label: "①講師のみ" },
                { value: "solo_students", label: "②単独＋生徒" },
                { value: "collab_students", label: "③コラボ＋生徒" },
              ]}
            />
            <Chipset
              value={fStatus}
              onChange={setFStatus}
              options={[
                { value: "all", label: "全状態" },
                { value: "selected", label: "採択" },
                { value: "pending", label: "選考中" },
                { value: "rejected", label: "不採択" },
              ]}
            />
            <select className="sel" value={fArea} onChange={(e) => setFArea(e.target.value)}>
              <option value="all">エリア：すべて</option>
              {AREAS.map((a) => (
                <option key={a}>{a}</option>
              ))}
            </select>
            <span className="spacer" />
            <button type="button" className="ghost-btn" onClick={exportWorksCsv}>
              CSV出力
            </button>
          </div>

          <div className={`layout${selectedWork ? "" : " solo"}`}>
            <div className="table-wrap">
              <div className="scroller">
                <div className="grid works-grid">
                  <div className="row head">
                    <span>企画の採否</span>
                    <span className="center">類型</span>
                    <span>
                      <button type="button" onClick={() => setSortKey("title")}>
                        作品 / ジャンル{" "}
                        <span className="arrow">{sortKey === "title" ? "↓" : "↕"}</span>
                      </button>
                    </span>
                    <span>インストラクター</span>
                    <span>
                      <button type="button" onClick={() => setSortKey("applicants")}>
                        出演応募{" "}
                        <span className="arrow">{sortKey === "applicants" ? "↓" : "↕"}</span>
                      </button>
                    </span>
                    <span>
                      {billingStarted ? (
                        <button type="button" onClick={() => setSortKey("paid")}>
                          出演者の入金{" "}
                          <span className="arrow">{sortKey === "paid" ? "↓" : "↕"}</span>
                        </button>
                      ) : (
                        "請求"
                      )}
                    </span>
                    <span>確定日程</span>
                    <span className="right">
                      <button type="button" onClick={() => setSortKey("updated")}>
                        更新 <span className="arrow">{sortKey === "updated" ? "↓" : "↕"}</span>
                      </button>
                    </span>
                  </div>

                  {filteredWorks.length === 0 && (
                    <div className="empty">条件に合う企画がありません</div>
                  )}

                  {filteredWorks.map((w) => {
                    const c = w.counts;
                    // 応募人数バーの基準。最大の企画に合わせて全行を同じ尺で描く。
                    const max = maxApplicants;
                    const solo = !w.recruits_students;
                    const payRatio = c.adopted ? c.paid / c.adopted : 0;
                    return (
                      <div
                        key={w.id}
                        className="row body"
                        role="button"
                        tabIndex={0}
                        aria-current={w.id === selectedWorkId}
                        onClick={() => setSelectedWorkId(w.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedWorkId(w.id);
                          }
                        }}
                      >
                        <StatusToggle
                          options={WORK_STATUS_OPTIONS}
                          value={w.status}
                          locked={c.billed > 0}
                          title={c.billed > 0 ? "請求を発行済みのため変更できません" : ""}
                          onChange={(v) => changeWorkStatus(w.id, v)}
                        />
                        <span className="center">
                          <span
                            className={`type-badge ${TYPE_CLASS[w.entry_type]}`}
                            title={TYPE_LABELS[w.entry_type]}
                          >
                            {TYPE_MARKS[w.entry_type]}
                          </span>
                        </span>
                        <div className="title-cell">
                          <div className="t">{w.title}</div>
                          <div className="cell-sub">{w.genre}</div>
                        </div>
                        <div className="instr">
                          {(w.instructors || []).map((i) => (
                            <span key={i.instructor_id} className={i.is_lead ? "lead" : ""}>
                              {i.name || `#${i.instructor_id}`}
                            </span>
                          ))}
                        </div>
                        <div>
                          {solo ? (
                            <span className="dash">生徒募集なし</span>
                          ) : (
                            <div className="applicants">
                              <div className="ap-top">
                                <span className="n">
                                  {billingStarted ? c.adopted : c.total}
                                </span>
                                <span className="u">名</span>
                                <span className="mini">
                                  {billingStarted
                                    ? `確定/${c.total}応募`
                                    : `会${c.member}・未${c.member_no_entry}・外${c.external}`}
                                </span>
                              </div>
                              <div className="ap-bar">
                                <i className="m" style={{ width: `${(c.member / max) * 100}%` }} />
                                <i
                                  className="n"
                                  style={{ width: `${(c.member_no_entry / max) * 100}%` }}
                                />
                                <i className="e" style={{ width: `${(c.external / max) * 100}%` }} />
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          {solo ? (
                            <span className="dash">—</span>
                          ) : !billingStarted ? (
                            <span className="pill unbilled">採択後に請求</span>
                          ) : (
                            <div className="pay">
                              <span className="frac">
                                {c.paid}
                                <em>/{c.adopted}</em>
                                {c.unpaid > 0 && (
                                  <span className="warn-dot" title={`未払い${c.unpaid}名`} />
                                )}
                              </span>
                              <div className="pay-bar">
                                <i className="ok" style={{ width: `${payRatio * 100}%` }} />
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          {w.schedule_count > 0 ? (
                            <div>
                              <span className="mono">{w.schedule_count}</span>回 確定
                              <div className="cell-sub">
                                {w.unbooked_count > 0 && (
                                  <span className="warn-text">未予約{w.unbooked_count} ・ </span>
                                )}
                                {planAreas(w).join("・") || "エリア未設定"}
                              </div>
                            </div>
                          ) : w.rehearsal_count > 0 ? (
                            <div>
                              <span className="dash">未登録</span>
                              <div className="cell-sub">
                                応募時の予定 {w.rehearsal_count}回 / {hours(w.rehearsal_minutes)}h
                              </div>
                            </div>
                          ) : (
                            <span className="dash">日程なし</span>
                          )}
                        </div>
                        <div className="right cell-sub flat">{w.updated_at}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {selectedWork && (
              <WorkDrawer
                work={selectedWork}
                rows={entriesByWork[selectedWork.id] || []}
                schedules={schedules.filter(
                  (s) => s.kind === "special" && s.special_work_id === selectedWork.id
                )}
                clashScheduleIds={clashScheduleIds}
                billingStarted={billingStarted}
                onClose={() => setSelectedWorkId(null)}
                onWorkStatus={changeWorkStatus}
                onEntryStatus={changeEntryStatus}
                onAddSchedule={() => {
                  setTab("sch");
                  openCreateSchedule("", {
                    kind: "special",
                    special_work_id: String(selectedWork.id),
                    venue_area: planAreas(selectedWork)[0] || "大阪",
                  });
                }}
              />
            )}
          </div>
        </section>
      )}

      {/* ============ 出演応募者 ============ */}
      {tab === "parts" && (
        <section>
          <div className="toolbar">
            <label className="search-bar">
              <Icon name="search" />
              <input
                type="text"
                value={qParts}
                placeholder="氏名・カナ・会員ID"
                onChange={(e) => setQParts(e.target.value)}
              />
            </label>
            <Chipset
              value={fKind}
              onChange={setFKind}
              options={[
                { value: "all", label: "全区分" },
                { value: "member", label: "①会員" },
                { value: "member_no_entry", label: "③未申込会員" },
                { value: "external", label: "②外部" },
              ]}
            />
            <Chipset
              value={fPay}
              onChange={setFPay}
              options={[
                { value: "all", label: "すべて" },
                { value: "unpaid", label: "未払いあり" },
                { value: "multi", label: "掛け持ち" },
                { value: "clash", label: "日程重複" },
              ]}
            />
            <select className="sel" value={fWork} onChange={(e) => setFWork(e.target.value)}>
              <option value="all">企画：すべて</option>
              {studentWorks.map((w) => (
                <option key={w.id} value={String(w.id)}>
                  {w.title}
                </option>
              ))}
            </select>
            <span className="spacer" />
            <button type="button" className="ghost-btn" onClick={exportEntrantsCsv}>
              CSV出力
            </button>
            <button type="button" className="primary-btn" onClick={loadInvoicePreview}>
              請求の対象を確認
            </button>
          </div>

          {!billingStarted ? (
            <div className="alert info">
              <Icon name="info circle" />
              <span>
                請求は<b>採択後</b>に人単位で1通にまとめて発行します。いまは全員 未請求です。
                ②外部と③未申込会員の<b>{summary.needs_entry_fee}名</b>には、特別ナンバー参加費とは別に
                発表会参加費 {yen(data.fees.entry_fee)}（税込）を請求します。掛け持ちは
                <b>{summary.entrants_multi}名</b>で、上限はありません。
              </span>
            </div>
          ) : (
            <div className="alert">
              <Icon name="warning sign" />
              <span>
                未払いが<b>{summary.unpaid}名</b>。確定日程が重なっている出演者が
                <b>{clashEntrantIds.size}名</b>います。日程タブで重複を確認してください。
              </span>
            </div>
          )}

          {invoicePreview && (
            <InvoicePreview
              preview={invoicePreview}
              onClose={() => setInvoicePreview(null)}
            />
          )}

          <div className="table-wrap">
            <div className="scroller">
              <div className="grid parts-grid">
                <div className="row head">
                  <span>氏名</span>
                  <span>区分</span>
                  <span>会員ID</span>
                  <span>連絡先</span>
                  <span>応募したナンバー</span>
                  <span className="center">支払方法</span>
                  <span>発表会参加費</span>
                  <span>特別ナンバー参加費</span>
                  <span className="right">応募日時</span>
                </div>

                {filteredEntrants.length === 0 && (
                  <div className="empty">条件に合う応募者がいません</div>
                )}

                {filteredEntrants.map((e) => (
                  <div key={e.id} className="row body static">
                    <div>
                      <div className="name">
                        {e.name}
                        {clashEntrantIds.has(e.id) && (
                          <span className="warn-dot" title="確定日程が重複" />
                        )}
                      </div>
                      <div className="cell-sub">{e.kana}</div>
                    </div>
                    <span>
                      <span className={`pill ${e.entrant_type}`} title={KIND_FULL[e.entrant_type]}>
                        {KIND_LABELS[e.entrant_type]}
                      </span>
                    </span>
                    <span className="mono dim">{e.customer_id || "—"}</span>
                    <div className="contact">
                      {e.mail ? (
                        <a className="mail" href={`mailto:${e.mail}`} title={e.mail}>
                          {e.mail}
                        </a>
                      ) : (
                        <span className="dash">メール未登録</span>
                      )}
                      {e.phone ? (
                        <span className="tel" title={e.phone}>
                          {e.phone}
                        </span>
                      ) : (
                        <span className="dash">電話未登録</span>
                      )}
                    </div>
                    <div className="chips">
                      {(e.entries || []).map((x) => {
                        const w = workById[x.work_id];
                        let cls = "";
                        let tip = "応募済み";
                        if (w && w.status !== "selected") {
                          cls = w.status === "rejected" ? "dropped" : "";
                          tip = w.status === "rejected" ? "企画が不採択" : "企画は選考中";
                        } else if (x.status === "adopted") {
                          cls = "adopted";
                          tip = ENTRY_LABELS.adopted;
                        } else if (x.status === "rejected") {
                          cls = "rejected";
                          tip = ENTRY_LABELS.rejected;
                        }
                        return (
                          <span key={x.id} className={cls} title={tip}>
                            {w ? w.title : `#${x.work_id}`}
                          </span>
                        );
                      })}
                    </div>
                    <span className="center cell-sub flat">
                      {e.payment_method === "lump" ? "一括" : "分割4回"}
                    </span>
                    <div>
                      <FeeCell
                        status={e.needs_entry_fee ? e.entry_fee_status || "unbilled" : ""}
                        amount={e.entry_fee_amount}
                        billingStarted={billingStarted}
                      />
                    </div>
                    <div>
                      <FeeCell
                        status={e.special_fee_status}
                        amount={e.special_fee_amount}
                        billingStarted={billingStarted}
                      />
                    </div>
                    <span className="right cell-sub flat">{e.applied_at}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ 日程 ============ */}
      {tab === "sch" && (
        <section>
          <div className="toolbar">
            <div className="month-nav">
              {MONTHS.map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={month === m}
                  onClick={() => setMonth(m)}
                >
                  {Number(m.slice(5))}月
                </button>
              ))}
            </div>
            <Chipset
              value={fSchKind}
              onChange={setFSchKind}
              options={[
                { value: "all", label: "全種別" },
                { value: "special", label: "特別ナンバー" },
                { value: "regional", label: "地方選抜" },
                { value: "headz", label: "HEADZ" },
              ]}
            />
            <Chipset
              value={showPlan ? "on" : "off"}
              onChange={(v) => setShowPlan(v === "on")}
              options={[
                { value: "on", label: "応募時の予定も重ねる" },
                { value: "off", label: "確定日程のみ" },
              ]}
            />
            <select className="sel" value={schArea} onChange={(e) => setSchArea(e.target.value)}>
              <option value="all">会場エリア：すべて</option>
              {AREAS.map((a) => (
                <option key={a}>{a}</option>
              ))}
            </select>
            <span className="spacer" />
            <span className="cal-legend">
              <span>
                <i className="c-special" />
                特別ナンバー
              </span>
              <span>
                <i className="c-regional" />
                地方選抜
              </span>
              <span>
                <i className="c-headz" />
                HEADZ
              </span>
              <span>
                <i className="c-plan" />
                応募時の予定
              </span>
            </span>
          </div>

          {conflicts.filter((c) => c.date.startsWith(month)).length > 0 && (
            <div className="alert">
              <Icon name="warning sign" />
              <span>
                出演者の重複が
                <b>{conflicts.filter((c) => c.date.startsWith(month)).length}件</b>。
                {conflicts
                  .filter((c) => c.date.startsWith(month))
                  .slice(0, 2)
                  .map(
                    (c) =>
                      `${c.date.slice(5).replace("-", "/")} ${c.work_titles[0]} と ${
                        c.work_titles[1]
                      }（${c.entrant_ids.length}名）`
                  )
                  .join("、")}
                。生徒応募ページのカレンダーにも同じ重なりが出ます。
              </span>
            </div>
          )}

          <div className="layout">
            <div className="table-wrap cal-wrap">
              <div className="cal">
                {WD.map((d) => (
                  <div key={d} className="dow">
                    {d}
                  </div>
                ))}
                {calendar.cells.map((d, i) => {
                  if (!d) return <div key={`e${i}`} className="day out" />;
                  const dow = i % 7;
                  return (
                    <div
                      key={d}
                      className={`day${dow === 6 ? " sat" : ""}${dow === 0 ? " sun" : ""}`}
                    >
                      <span className="dn">{d}</span>
                      {(calendar.byDay[d] || []).map((ev) =>
                        ev.type === "schedule" ? (
                          <button
                            key={`s${ev.schedule.id}`}
                            type="button"
                            className={`ev k-${ev.schedule.kind}${
                              ev.schedule.is_booked ? "" : " tent"
                            }${clashScheduleIds.has(ev.schedule.id) ? " clash" : ""}`}
                            title={`${ev.schedule.name} ${ev.schedule.start_time}–${
                              ev.schedule.end_time
                            } ${ev.schedule.studio_name || "スタジオ未定"}`}
                            onClick={() => openEditSchedule(ev.schedule)}
                          >
                            <span className="h">{ev.schedule.start_time || "時刻未定"}</span>
                            {ev.schedule.name}
                          </button>
                        ) : (
                          <span
                            key={`p${ev.work.id}${ev.plan.date}${ev.plan.start_time}`}
                            className="ev plan"
                            title={`応募時の予定：${ev.work.title}`}
                          >
                            <span className="h">{ev.plan.start_time}</span>
                            {ev.work.title}
                          </span>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <aside className="side-card">
              <header>
                <h2>{Number(month.slice(5))}月の日程</h2>
                <span className="cell-sub flat">{monthSchedules.length}コマ</span>
              </header>
              <div className="side-body">
                <section className="d-sec">
                  <h3>
                    日程<span className="aux">地方選抜・HEADZ・特別ナンバー</span>
                  </h3>
                  <div className="list scroll tall">
                    {monthSchedules.length === 0 && (
                      <div className="li">
                        <span className="dash">この月の日程はありません</span>
                      </div>
                    )}
                    {monthSchedules.map((s) => (
                      <div
                        key={s.id}
                        className={`li sch${clashScheduleIds.has(s.id) ? " conflict" : ""}`}
                      >
                        <span className="dt">
                          {Number(s.date.slice(5, 7))}/{Number(s.date.slice(8, 10))}
                          <span className="wd">{s.weekday}</span>
                        </span>
                        <span className="mid" title={`${schKindLabel(s)} ${s.name}`}>
                          <span className={`kind-dot k-${s.kind}`} />
                          <span className="tm">
                            {s.start_time || "--:--"}–{s.end_time || "--:--"}
                          </span>{" "}
                          <span className="sub">
                            {s.name} ・ {s.studio_name || "会場未定"}
                            {s.venue_area ? `（${s.venue_area}）` : ""}
                            {s.note ? ` ・ ${s.note}` : ""}
                          </span>
                        </span>
                        <span className="row-actions">
                          {s.is_booked ? (
                            <span className="pill paid">予約済</span>
                          ) : (
                            <span className="pill tent">未予約</span>
                          )}
                          {/* 右パネルは幅が狭いので、リハーサル日程ページの「✎ 編集」「🗑 削除」を
                              記号だけにする（名前は title と aria-label で残す）。 */}
                          <button
                            type="button"
                            className="mini-btn icon"
                            title="編集"
                            aria-label="編集"
                            onClick={() => openEditSchedule(s)}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="mini-btn icon danger"
                            title="削除"
                            aria-label="削除"
                            onClick={() => setDeleteTarget(s)}
                          >
                            🗑
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="add-line"
                    onClick={() => openCreateSchedule("")}
                  >
                    ＋ 日程を追加
                  </button>
                </section>
              </div>
            </aside>
          </div>
        </section>
      )}

      <p className="foot-note">
        請求書の発行（分割払いのサブスク開始を含む）はこの画面からは行わず、サーバーで管理コマンド
        <code>issue_recital2027_special_invoices</code> を実行して行います。
      </p>
    </div>

    {/* 日程の入力と削除の確認は、リハーサル日程ページの部品とスタイルを共用する。
        スタイルがそのページのルートクラス配下に書かれているので同じクラスの枠で包む
        （中身はすべて position: fixed。枠自体は display: contents で場所を取らない）。 */}
    <div className="recital2027-rehearsal-schedule-page special-schedule-overlay">
      <div
        className={`sch-mask ${formOpen ? "show" : ""}`}
        onClick={() => setFormOpen(false)}
      />
      <ScheduleFormDrawer
        open={formOpen}
        target={formTarget}
        defaultDate={formDate}
        defaults={formDefaults}
        works={formWorks}
        onClose={() => setFormOpen(false)}
        onSaved={handleScheduleSaved}
      />
      <div className={`confirm ${deleteTarget ? "show" : ""}`}>
        <div className="confirm-box">
          <h3>この日程を削除しますか？</h3>
          <p>
            削除すると一覧・カレンダーから消えます。
            <br />
            データは残るので、必要なら復元できます。
          </p>
          {deleteTarget && (
            <div className="target" style={{ "--tone": schTone(deleteTarget) }}>
              {(() => {
                const d = new Date(`${deleteTarget.date}T00:00:00`);
                return `${d.getMonth() + 1}/${d.getDate()}（${WD[d.getDay()]}）${
                  deleteTarget.start_time || ""
                }–${deleteTarget.end_time || ""}`;
              })()}
              <br />
              {schKindLabel(deleteTarget)}
              {deleteTarget.kind === "special" && deleteTarget.name
                ? ` / ${deleteTarget.name}`
                : ""}
              　{deleteTarget.studio_name || "会場未定"}
            </div>
          )}
          <div className="f-actions">
            <button type="button" className="btn" onClick={() => setDeleteTarget(null)}>
              キャンセル
            </button>
            <button
              type="button"
              className="btn danger"
              onClick={handleScheduleDelete}
              disabled={deleting}
            >
              {deleting ? "削除中…" : "削除する"}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* 企画ドロワー                                                         */
/* ------------------------------------------------------------------ */
const WorkDrawer = ({
  work,
  rows,
  schedules,
  clashScheduleIds,
  billingStarted,
  onClose,
  onWorkStatus,
  onEntryStatus,
  onAddSchedule,
}) => {
  const c = work.counts;
  const solo = !work.recruits_students;
  const locked = work.status !== "selected";
  const billed = c.billed > 0;
  const honor = solo ? "なし（ボランティア）" : "40,000円";
  const studio = solo ? "自己負担" : "会社負担・上限50,000円";

  return (
    <aside className="drawer">
      <div className="drawer-head">
        <div className="top">
          <div>
            <h2>{work.title}</h2>
            <p className="meta">
              {work.genre}　#{work.id}　{TYPE_LABELS[work.entry_type]}
            </p>
          </div>
          <button type="button" className="close-btn" onClick={onClose} title="閉じる">
            ✕
          </button>
        </div>
        <StatusToggle
          options={WORK_STATUS_OPTIONS}
          value={work.status}
          locked={billed}
          title={billed ? "請求を発行済みのため変更できません" : ""}
          onChange={(v) => onWorkStatus(work.id, v)}
        />
      </div>

      <div className="drawer-body">
        {billed && (
          <div className="alert info">
            <Icon name="info circle" />
            <span>
              請求を発行済みのため、採否はこの画面から変更できません。イレギュラーな取り消しは個別対応です。
            </span>
          </div>
        )}
        {billingStarted && !solo && c.unpaid > 0 && (
          <div className="alert">
            <Icon name="warning sign" />
            <span>
              出演確定{c.adopted}名のうち<b>{c.unpaid}名</b>が未払いです。
            </span>
          </div>
        )}

        <section className="d-sec">
          <h3>
            企画<span className="aux">イントラ側で編集</span>
          </h3>
          <dl className="kv">
            <dt>応募者</dt>
            <dd>
              <span className="instr">
                {(work.instructors || [])
                  .filter((i) => i.is_lead)
                  .map((i) => (
                    <span key={i.instructor_id} className="lead">
                      {i.name || `#${i.instructor_id}`}
                    </span>
                  ))}
              </span>
            </dd>
            {(work.instructors || []).filter((i) => !i.is_lead).length > 0 && (
              <>
                <dt>共演</dt>
                <dd>
                  <span className="instr">
                    {(work.instructors || [])
                      .filter((i) => !i.is_lead)
                      .map((i) => (
                        <span key={i.instructor_id}>{i.name || `#${i.instructor_id}`}</span>
                      ))}
                  </span>
                </dd>
              </>
            )}
            <dt>更新</dt>
            <dd className="mono">{work.updated_at}</dd>
          </dl>
          <p className="concept">{work.concept}</p>
          {work.note && (
            <dl className="kv">
              <dt>備考</dt>
              <dd>{work.note}</dd>
            </dl>
          )}
        </section>

        <section className="d-sec">
          <h3>条件</h3>
          <div className="money-row">
            <div className="money">
              <span className="k">謝礼</span>
              <span className="v">{honor}</span>
            </div>
            <div className="money">
              <span className="k">スタジオ代</span>
              <span className="v small">{studio}</span>
            </div>
          </div>
        </section>

        {!solo && (
          <section className="d-sec">
            <h3>
              出演者
              <span className="aux">
                {locked
                  ? "企画の採択後に確定します"
                  : `出演確定 ${c.adopted} / 応募 ${c.total}${
                      c.rejected ? ` ・ 不採用 ${c.rejected}` : ""
                    }`}
              </span>
            </h3>
            {locked ? (
              <div className="alert info">
                <Icon name="info circle" />
                <span>
                  選考は2段階です。企画を<b>採択</b>にすると、ここの応募者は全員 出演確定になります。
                </span>
              </div>
            ) : (
              !billed && (
                <div className="alert info">
                  <Icon name="info circle" />
                  <span>
                    採択済みなので全員が出演確定です。<b>定員を超えたときだけ</b>
                    個別に不採用へ外してください。
                  </span>
                </div>
              )
            )}
            <div className="list scroll tall">
              {rows.length === 0 && (
                <div className="li">
                  <span className="dash">まだ応募がありません</span>
                </div>
              )}
              {rows.map((r) => (
                <div key={r.id} className="li ent">
                  <span className="nm">
                    {r.entrant.name}
                    <span className="sub">{r.entrant.kana}</span>
                  </span>
                  <span className={`pill ${r.entrant.entrant_type}`}>
                    {KIND_LABELS[r.entrant.entrant_type]}
                  </span>
                  <StatusToggle
                    options={ENTRY_STATUS_OPTIONS}
                    value={r.status}
                    locked={locked || r.entrant.billed}
                    title={r.entrant.billed ? "請求を発行済みのため変更できません" : ""}
                    onChange={(v) => onEntryStatus(r.id, v)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="d-sec">
          <h3>
            応募時のリハーサル予定<span className="aux">生徒に公開済み</span>
          </h3>
          {work.rehearsals && work.rehearsals.length > 0 ? (
            <div className="list">
              {work.rehearsals.map((p, i) => (
                <div key={`${p.date}-${p.start_time}-${i}`} className="li plan">
                  <span className="dt">
                    {Number(p.date.slice(5, 7))}/{Number(p.date.slice(8, 10))}
                    <span className="wd">{p.weekday}</span>
                  </span>
                  <span className="tm">
                    {p.start_time} – {p.end_time}
                    <span className="len">{hours(p.minutes)}h</span>
                  </span>
                  <span className="ar">{p.area}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="dash flat">応募時のリハーサル予定はありません。</p>
          )}
        </section>

        <section className="d-sec">
          <h3>確定日程</h3>
          {schedules.length > 0 ? (
            <div className="list">
              {schedules.map((s) => (
                <div
                  key={s.id}
                  className={`li sch${clashScheduleIds.has(s.id) ? " conflict" : ""}`}
                >
                  <span className="dt">
                    {Number(s.date.slice(5, 7))}/{Number(s.date.slice(8, 10))}
                    <span className="wd">{s.weekday}</span>
                  </span>
                  <span className="mid">
                    <span className="tm">
                      {s.start_time || "--:--"}–{s.end_time || "--:--"}
                    </span>{" "}
                    <span className="sub">
                      {s.studio_name || "スタジオ未定"}
                      {s.note ? ` ・ ${s.note}` : ""}
                    </span>
                  </span>
                  <span>
                    {s.is_booked ? (
                      <span className="pill paid">予約済</span>
                    ) : (
                      <span className="pill tent">未予約</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="dash flat">確定日程はまだ登録されていません。</p>
          )}
          {work.status === "selected" && !solo && (
            <button type="button" className="add-line" onClick={onAddSchedule}>
              ＋ 日程タブで追加する
            </button>
          )}
        </section>
      </div>
    </aside>
  );
};

/* ------------------------------------------------------------------ */
/* 請求プレビュー                                                       */
/* ------------------------------------------------------------------ */
const InvoicePreview = ({ preview, onClose }) => {
  const s = preview.summary;
  return (
    <div className="invoice-preview">
      <header>
        <h2>請求の対象と金額</h2>
        <button type="button" className="close-btn" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="money-row">
        <div className="money">
          <span className="k">請求対象</span>
          <span className="v">{s.billable}名</span>
        </div>
        {s.entry_fee_only > 0 && (
          <div className="money">
            <span className="k">発表会参加費のみ未発行</span>
            <span className="v">{s.entry_fee_only}名</span>
          </div>
        )}
        <div className="money">
          <span className="k">一括 / 分割</span>
          <span className="v">
            {s.lump} / {s.installment}
          </span>
        </div>
        <div className="money">
          <span className="k">特別ナンバー参加費</span>
          <span className="v small">{yen(s.special_fee_total)}</span>
        </div>
        <div className="money">
          <span className="k">発表会参加費</span>
          <span className="v small">{yen(s.entry_fee_total)}</span>
        </div>
        <div className="money">
          <span className="k">発行済み</span>
          <span className="v">{s.already_billed}名</span>
        </div>
      </div>
      <p className="foot-note small">
        金額はすべて税込です。採択確定時に固定され、あとから直せません。実際の発行はサーバー上で
        <code>python manage.py issue_recital2027_special_invoices --dry-run</code>
        で内容を確認してから、<code>--dry-run</code> を外して実行してください。一括と発表会参加費の支払期限は
        {preview.due_date}（発行日に関係なく共通）です。分割は{preview.installment_start}
        から毎月1日に請求し、それぞれ{preview.installment_due_days}日後が期限の4回払いです。
      </p>
      <div className="list scroll tall">
        {preview.targets.slice(0, 80).map((t) => (
          <div key={t.entrant_id} className={`li tgt${t.amount_to_issue ? "" : " off"}`}>
            <span className="nm">{t.name}</span>
            <span className="cell-sub flat">
              {t.payment_method === "lump" ? "一括" : "分割4回"} ・ {t.adopted_works}ナンバー
            </span>
            <span className="mono">{yen(t.amount_to_issue)}</span>
            <span className="cell-sub flat">{t.skip_reason || "対象"}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpecialWorkPage;
